import { moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import {
  DEFAULT_FOOTER,
  EEntityType,
  EFooterBlockType,
  ERelationType,
  ETextType,
  FOOTER_BLOCK_TYPE_OPTIONS,
  IFooter,
  IFooterBlock,
  FooterBlockId,
  ImageId,
  INormalText,
  ITextLanguageValue,
  LanguagePrefix,
  NormalTextId,
  TextId,
  TextLanguageValueId,
} from '@web/dtos';
import { map, Observable, Subscription } from 'rxjs';
import { ButtonComponent } from '../../../components/feedback/button/button.component';
import { IDialogData } from '../../../components/dialogs/dialog';
import { ResetChangesDialogComponent } from '../../../components/dialogs/reset-changes-dialog/reset-changes-dialog.component';
import { FooterComponent } from '../../../components/data-display/footer/footer.component';
import { ImageSelectComponent } from '../../../components/data-input/form-inputs/image-select/image-select.component';
import { PaymentSelectComponent } from '../../../components/data-input/form-inputs/payment-select/payment-select.component';
import { TextInputComponent } from '../../../components/data-input/form-inputs/text-input/text-input.component';
import { TranslationAccessibilityTextInputComponent } from '../../../components/data-input/form-inputs/translation-accessibility-text-input/translation-accessibility-text-input.component';
import { TranslationTextAreaComponent } from '../../../components/data-input/form-inputs/translation-text-area/translation-text-area.component';
import { TranslationTextInputComponent } from '../../../components/data-input/form-inputs/translation-text-input/translation-text-input.component';
import { LoadingAnimationComponent } from '../../../components/feedback/loading-animation/loading-animation.component';
import { PreviewComponent } from '../../../components/feedback/preview/preview.component';
import { RevealFormErrorsButtonComponent } from '../../../components/feedback/reveal-form-errors-button/reveal-form-errors-button.component';
import { SectionHeaderActionsComponent } from '../../../components/layout/section/section-header/section-header-actions/section-header-actions.component';
import { SectionHeaderComponent } from '../../../components/layout/section/section-header/section-header.component';
import { SectionHeadlineComponent } from '../../../components/layout/section/section-header/section-headline/section-headline.component';
import { SectionComponent } from '../../../components/layout/section/section.component';
import { ECMSFontSize } from '../../../constants/buttonGroups.enums';
import { IFooterBlockForm, IFooterForm } from '../../../entities/footer-entity';
import { IAccessibleImageForm } from '../../../entities/image-entity';
import { ILinkForm } from '../../../entities/link-entity';
import { FooterFormsFactoryService } from '../../../factory/footer/footer-forms-factory.service';
import { ImageFormsFactoryService } from '../../../factory/image/image-forms-factory.service';
import { LinkFormsFactoryService } from '../../../factory/link/link-forms-factory.service';
import { FooterStore } from '../../../services/api/footer/footer-store.service';
import { PaymentProviderStore } from '../../../services/api/payment-provider/payment-provider-store.service';
import { FormTracking } from '../form-tracking/form-tracking.component';
import { ElementFocusService } from '../../../services/support/element-focus-support/element-focus-support.service';
import { DialogService } from '../../../services/support/dialog/dialog.service';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';
import { DragDropGrid } from '../../../components/data-input/drag-drop-grid/drag-drop-grid';
import { DropDownComponent } from '../../../components/data-input/form-inputs/drop-down/drop-down.component';
import { CheckboxComponent } from '../../../components/data-input/form-inputs/checkbox/checkbox.component';
import {
  ButtonGroupComponent,
  IButtonOption,
} from '../../../components/data-input/form-inputs/button-group/button-group.component';
import { ColorPickerInputComponent } from '../../../components/data-input/form-inputs/color-picker-input/color-picker-input.component';
import { EnumToButtonOptionArrayPipe } from '../../../pipes/enum-to-button-option-array/enum-to-button-option-array.pipe';
import { ColorPickerListComponent } from '../../../components/data-input/color-picker-list/color-picker-list';

@Component({
  selector: 'aw-footer-page',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SectionComponent,
    ButtonComponent,
    SectionHeaderComponent,
    SectionHeaderActionsComponent,
    SectionHeadlineComponent,
    TextInputComponent,
    ImageSelectComponent,
    PaymentSelectComponent,
    TranslationTextInputComponent,
    TranslationTextAreaComponent,
    TranslationAccessibilityTextInputComponent,
    LoadingAnimationComponent,
    RevealFormErrorsButtonComponent,
    PreviewComponent,
    DragDropGrid,
    DropDownComponent,
    CheckboxComponent,
    ButtonGroupComponent,
    ColorPickerInputComponent,
    EnumToButtonOptionArrayPipe,
    RouterLink,
    ColorPickerListComponent
  ],
  templateUrl: './footer-page.component.html',
  styles: ``,
})
export class FooterPageComponent
  extends FormTracking<IFooterForm>
  implements OnInit, OnDestroy
{
  private readonly elementFocusService: ElementFocusService =
    inject(ElementFocusService);
  readonly footerStore: FooterStore = inject(FooterStore);
  readonly formBuilder: FormBuilder = inject(FormBuilder);
  readonly footerFormsFactory: FooterFormsFactoryService = inject(
    FooterFormsFactoryService
  );

  readonly imageFormsFactory: ImageFormsFactoryService = inject(
    ImageFormsFactoryService
  );
  readonly linkFormsFactory: LinkFormsFactoryService = inject(
    LinkFormsFactoryService
  );
  readonly paymentProviderStore: PaymentProviderStore =
    inject(PaymentProviderStore);
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  private readonly dialogService: DialogService = inject(DialogService);

  protected footer: IFooter | undefined;
  public readonly hiddenPaymentAreaImages: FormArray<
    FormGroup<IAccessibleImageForm>
  > = this.formBuilder.array<FormGroup<IAccessibleImageForm>>([]);
  protected readonly EntityType: typeof EEntityType = EEntityType;
  protected readonly footerBorderColors: typeof ECMSFontSize = ECMSFontSize;
  public readonly backgroundColorBtn: FormControl<string> =
    new FormControl<string>('') as FormControl<string>;
  public footerForm!: FormGroup<IFooterForm>;
  public languageForm: FormGroup = new FormGroup({
    singleSelect: new FormControl('DE', [Validators.required]),
  });
  public readonly minimumFooterLinks: number = 2;
  public readonly maximumFooterBlocks: number = 7;
  private readonly fixedFooterBlockCount: number = 4;
  public readonly maximumCustomFooterBlocks: number =
    this.maximumFooterBlocks - this.fixedFooterBlockCount;
  protected readonly TextRelationType: typeof ERelationType = ERelationType;
  protected readonly footerBlockTypeOptions: typeof FOOTER_BLOCK_TYPE_OPTIONS =
    FOOTER_BLOCK_TYPE_OPTIONS;
  protected readonly FooterBlockType: typeof EFooterBlockType =
    EFooterBlockType;
  protected readonly orientationOptions: IButtonOption<
    'vertical' | 'horizontal'
  >[] = [
    { label: 'Vertikal', value: 'vertical' },
    { label: 'Horizontal', value: 'horizontal' },
  ];

  private readonly subscription: Subscription = new Subscription();

  protected readonly FooterComponent: typeof FooterComponent = FooterComponent;
  previewData: WritableSignal<IFooter[]> = signal([]);
  public currentColor: string = '';
  private readonly mandatoryFooterLinkPaths: Set<string> = new Set<string>([
    'impressum',
    'datenschutz',
  ]);

  public isMandatoryFooterLink(linkFormGroup: FormGroup<ILinkForm>): boolean {
    const urls: INormalText[] = linkFormGroup.controls.urls.value;

    return urls.some((url: INormalText): boolean =>
      url.text.values.some((value: ITextLanguageValue): boolean =>
        this.mandatoryFooterLinkPaths.has(this.getLinkPath(value.value))
      )
    );
  }

  protected getIconForBlockType(blockType: EFooterBlockType): string {
    switch (blockType) {
      case EFooterBlockType.Logo:
        return 'image';
      case EFooterBlockType.Images:
        return 'table_rows';
      case EFooterBlockType.Payments:
        return 'payments';
      case EFooterBlockType.Links:
        return 'link';
      case EFooterBlockType.Text:
        return 'text_fields';
      default:
        return 'article';
    }
  }

  public getBlockLetter(index: number): string {
    return String.fromCharCode(65 + index + this.fixedFooterBlockCount);
  }

  private getLinkPath(value: string): string {
    return (
      value
        .trim()
        .toLowerCase()
        .split('?')[0]
        .replace(/\/+$/, '')
        .split('/')
        .pop() ?? ''
    );
  }

  // Color picker popup state
  public moveLayoutBlock(index: number, newIndex: number): void {
    const blockOrder = this.footerForm.controls.layout.controls.blockOrder;
    const values = [...blockOrder.value];

    moveItemInArray(values, index, newIndex);

    blockOrder.clear();
    values.forEach(value => {
      blockOrder.push(this.formBuilder.nonNullable.control(value));
    });

    this.footerForm.markAsDirty();
  }
  public getLinkTitle(linkFormGroup: FormGroup): string {
    const texts = linkFormGroup.get('texts')?.value;
    return texts?.[0]?.text?.values?.[0]?.value || 'Beschreibung';
  }
  public toggleBackgroundImage(): void {
    const control =
      this.footerForm.controls.appearance.controls.showBackgroundImage;

    control.setValue(!control.value);
    this.footerForm.markAsDirty();
  }
  public onHiddenBlocksChanged(hiddenBlocks: string[]): void {
    const hiddenBlocksControl =
      this.footerForm.controls.layout.controls.hiddenBlocks;

    hiddenBlocksControl.clear();
    hiddenBlocks.forEach(id =>
      hiddenBlocksControl.push(this.formBuilder.nonNullable.control(id))
    );

    this.updateSectionStatesBasedOnHiddenBlocks(hiddenBlocks);
    this.footerForm.markAsDirty();
  }

  public onBlockOrderChanged(blockOrder: string[]): void {
    const blockOrderControl =
      this.footerForm.controls.layout.controls.blockOrder;

    blockOrderControl.clear();
    blockOrder.forEach(id =>
      blockOrderControl.push(this.formBuilder.nonNullable.control(id))
    );

    this.footerForm.markAsDirty();
  }
  public addBlock(): void {
    const blocks = this.footerForm.controls.blocks;
    if (blocks.length >= this.maximumCustomFooterBlocks) {
      return;
    }
    const block = this.footerFormsFactory.buildBlockForm(
      this.createEmptyBlock(blocks.length + this.fixedFooterBlockCount),
      blocks.length + this.fixedFooterBlockCount
    );
    blocks.push(block);
    this.footerForm.controls.layout.controls.blockOrder.push(
      this.formBuilder.nonNullable.control(block.controls.id.value as string)
    );
    this.footerForm.markAsDirty();
  }

  public removeBlock(index: number): void {
    const blocks = this.footerForm.controls.blocks;
    const blockId = blocks.at(index).controls.id.value;

    blocks.removeAt(index);
    this.removeLayoutBlock(blockId);
    this.updateBlockPositions();
    this.footerForm.markAsDirty();
  }

  public moveBlock(index: number, newIndex: number): void {
    this.moveItemAtIndex(this.footerForm.controls.blocks, index, newIndex);
    this.updateBlockPositions();
  }

  public addBlockImage(block: FormGroup<IFooterBlockForm>): void {
    block.controls.accessibleImages.push(
      this.imageFormsFactory.buildImageFormGroup(
        undefined,
        block.controls.accessibleImages.length
      )
    );
    this.footerForm.markAsDirty();
  }

  public addBlockLink(block: FormGroup<IFooterBlockForm>): void {
    block.controls.links.push(
      this.linkFormsFactory.buildLinkFormGroup(
        undefined,
        block.controls.links.length
      )
    );
    this.footerForm.markAsDirty();
  }

  public removeBlockImage(
    block: FormGroup<IFooterBlockForm>,
    imageIndex: number
  ): void {
    block.controls.accessibleImages.removeAt(imageIndex);
    this.footerForm.markAsDirty();
  }

  public getFirstImageControl(block: FormGroup<IFooterBlockForm>) {
    if (block.controls.accessibleImages.length === 0) {
      this.addBlockImage(block);
    }
    return block.controls.accessibleImages.at(0);
  }

  public getFirstLogoControl() {
    if (
      this.footerForm.controls.logoArea.controls.accessibleImages.length === 0
    ) {
      this.addLogo();
    }
    return this.footerForm.controls.logoArea.controls.accessibleImages.at(0);
  }

  public addLogo(): void {
    this.footerForm.controls.logoArea.controls.accessibleImages.push(
      this.imageFormsFactory.buildImageFormGroup(
        undefined,
        this.footerForm.controls.logoArea.controls.accessibleImages.length
      )
    );
    this.footerForm.markAsDirty();
  }

  private createEmptyBlock(positionOrder: number): IFooterBlock {
    return {
      id: crypto.randomUUID() as FooterBlockId,
      type: EFooterBlockType.Images,
      orientation: 'vertical',
      isActive: true,
      positionOrder,
      headlines: [],
      texts: [],
      accessibleImages: [],
      links: [],
    };
  }

  private updateBlockPositions(): void {
    this.footerForm.controls.blocks.controls.forEach((block, index) =>
      block.controls.positionOrder.setValue(
        index + this.fixedFooterBlockCount,
        { emitEvent: false }
      )
    );
  }

  public getCustomLayoutBlocks(): Array<{
    id: string;
    name: string;
    icon: string;
  }> {
    return this.footerForm.controls.blocks.controls.map((block, index) => ({
      id: block.controls.id.value as string,
      name: `${this.cmsTextProvider.footer.block} ${this.getBlockLetter(index)}`,
      icon: this.getIconForBlockType(block.controls.type.value),
    }));
  }

  private removeLayoutBlock(blockId: FooterBlockId | null): void {
    if (!blockId) {
      return;
    }

    const removeId = (formArray: FormArray<FormControl<string>>): void => {
      const index = formArray.controls.findIndex(
        control => control.value === blockId
      );
      if (index !== -1) {
        formArray.removeAt(index);
      }
    };

    removeId(this.footerForm.controls.layout.controls.blockOrder);
    removeId(this.footerForm.controls.layout.controls.hiddenBlocks);
  }
  private updateSectionStatesBasedOnHiddenBlocks(hiddenBlocks: string[]): void {
    const isLogoHidden = hiddenBlocks.includes('logoArea');
    const isAddressHidden = hiddenBlocks.includes('addressArea');
    const isPaymentHidden = hiddenBlocks.includes('paymentArea');

    if (isLogoHidden) {
      this.footerForm.controls.logoArea.disable();
    } else {
      this.footerForm.controls.logoArea.enable();
    }

    if (isAddressHidden) {
      this.footerForm.controls.addressArea.disable();
    } else {
      this.footerForm.controls.addressArea.enable();
    }

    if (isPaymentHidden) {
      this.footerForm.controls.paymentArea.disable();
    } else {
      this.footerForm.controls.paymentArea.enable();
    }
  }

  constructor() {
    super();
    effect(() => {
      this.footer = this.footerStore.footer();
      this.footerForm = this.footerFormsFactory.buildForm(
        this.deepCopyService.createDeepCopy(this.footer)
      );
      this.initHiddenPaymentImages();
      this.previewData = signal([this.footer]);
      this.initFormTracking(this.footerForm);
      this.setBackgroundColorBtnState(this.footerStore.footer());
      this.createActivitySubscription();
      this.footerForm.controls.logoArea.controls.isActive.updateValueAndValidity();
      this.footerForm.controls.paymentArea.controls.isActive.updateValueAndValidity();
      this.footerForm.controls.addressArea.controls.isActive.updateValueAndValidity();
      this.createPreviewListener();
    });
    effect(() => {
      const providers = this.paymentProviderStore.paymentProviders();
      if (
        this.footerForm &&
        providers.length > 0 &&
        this.hiddenPaymentAreaImages.length === 0
      ) {
        this.initHiddenPaymentImages();
      }
    });
    effect(() => {
      this.elementFocusService.focusElement(
        !this.footerStore.isLoading() && !!this.footerStore.footer()
      );
    });
  }
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    window.onbeforeunload = (event: BeforeUnloadEvent) => {
      if (this.footerForm.dirty || this.footerForm.controls.logoArea.touched) {
        event.preventDefault();
      }
    };
  }

  private createPreviewListener(): void {
    this.subscription.add(
      this.footerForm.valueChanges.subscribe((): void => {
        this.previewData.set([
          this.footerFormsFactory.buildEntity(this.footerForm),
        ]);
      })
    );
  }

  createActivitySubscription(): void {
    this.subscription.add(
      this.footerForm.controls.logoArea.controls.isActive.valueChanges.subscribe(
        (isActive: boolean): void => {
          if (isActive) {
            this.footerForm.controls.logoArea.controls.headlines.enable();
            this.footerForm.controls.logoArea.controls.accessibleImages.enable();
          } else {
            this.footerForm.controls.logoArea.controls.headlines.disable();
            this.footerForm.controls.logoArea.controls.accessibleImages.disable();
          }
        }
      )
    );

    this.subscription.add(
      this.footerForm.controls.paymentArea.controls.isActive.valueChanges.subscribe(
        (isActive: boolean): void => {
          if (isActive) {
            this.footerForm.controls.paymentArea.controls.headlines.enable();
            this.footerForm.controls.paymentArea.controls.accessibleImages.enable();
          } else {
            this.footerForm.controls.paymentArea.controls.headlines.disable();
            this.footerForm.controls.paymentArea.controls.accessibleImages.disable();
          }
        }
      )
    );
    this.subscription.add(
      this.footerForm.controls.addressArea.controls.isActive.valueChanges.subscribe(
        (isActive: boolean): void => {
          if (isActive) {
            this.footerForm.controls.addressArea.controls.headlines.enable();
            this.footerForm.controls.addressArea.controls.texts.enable();
          } else {
            this.footerForm.controls.addressArea.controls.headlines.disable();
            this.footerForm.controls.addressArea.controls.texts.disable();
          }
        }
      )
    );
  }

  deleteLogo(index: number): void {
    this.footerForm.controls.logoArea.controls.accessibleImages.removeAt(index);
    this.footerForm.markAsDirty();
  }

  public initHiddenPaymentImages(): void {
    this.hiddenPaymentAreaImages.clear();
    const availableProviders = this.paymentProviderStore.paymentProviders();
    if (
      !availableProviders ||
      availableProviders.length === 0 ||
      !this.footerForm
    ) {
      return;
    }
    const visibleImages =
      this.footerForm.controls.paymentArea.controls.accessibleImages.controls;

    availableProviders.forEach(provider => {
      const isVisible = visibleImages.some(ctrl => {
        const imgVal = ctrl.controls.image.value;
        return (
          imgVal?.id === provider.id ||
          imgVal?.filename?.toLowerCase() === provider.filename.toLowerCase()
        );
      });

      if (!isVisible) {
        const hiddenGroup = this.imageFormsFactory.buildImageFormGroup(
          {
            image: {
              ...(provider.id ? { id: provider.id as unknown as ImageId } : {}),
              filename: provider.filename,
              fileExtension: provider.fileExtension,
              createdAt: new Date(),
            },
            imageAlts: [
              {
                id: crypto.randomUUID() as NormalTextId,
                textType: ETextType.Web,
                relationType: ERelationType.imageAlt,
                entityType: EEntityType.footerPaymentArea,
                text: {
                  id: crypto.randomUUID() as TextId,
                  values: [
                    {
                      id: crypto.randomUUID() as TextLanguageValueId,
                      value: provider.filename,
                      languagePrefix: 'DE' as LanguagePrefix,
                    },
                  ],
                },
              },
            ],
            url: '',
            positionOrder: this.hiddenPaymentAreaImages.length,
          },
          this.hiddenPaymentAreaImages.length
        );
        this.hiddenPaymentAreaImages.push(hiddenGroup);
      }
    });
  }

  hidePayment(index: number): void {
    const item =
      this.footerForm.controls.paymentArea.controls.accessibleImages.at(index);
    this.footerForm.controls.paymentArea.controls.accessibleImages.removeAt(
      index
    );
    this.hiddenPaymentAreaImages.push(item);
    this.footerForm.markAsDirty();
  }

  showPayment(index: number): void {
    const item = this.hiddenPaymentAreaImages.at(index);
    this.hiddenPaymentAreaImages.removeAt(index);
    this.footerForm.controls.paymentArea.controls.accessibleImages.push(item);
    this.footerForm.markAsDirty();
  }

  addLink(event: MouseEvent): void {
    (event.target as HTMLElement).blur();
    this.footerForm.controls.linkArea.controls.links.push(
      this.linkFormsFactory.buildLinkFormGroup(
        undefined,
        this.footerForm.controls.linkArea.controls.links.controls.length
      )
    );
    this.footerForm.markAsDirty();
  }

  deleteLink(index: number): void {
    this.footerForm.controls.linkArea.controls.links.removeAt(index);
    this.footerForm.markAsDirty();
  }

  onBackgroundColorChange(value: string): void {
    switch (value) {
      case ECMSFontSize.Benutzerdefiniert:
        this.footerForm.controls.backgroundColor.enable();
        break;
      case ECMSFontSize.Standard:
        this.footerForm.controls.backgroundColor.setValue(
          DEFAULT_FOOTER.backgroundColor
        );
        this.footerForm.controls.backgroundColor.disable();
        break;
    }
    this.footerForm.markAsDirty();
  }

  onColorChosen(color: string, key: string): void {
    (this.footerForm.get('appearance') as FormGroup)
      .get(key)
      ?.patchValue('#' + color);

    this.footerForm.markAsDirty();
  }

  setBackgroundColorBtnState(footer: IFooter): void {
    if (footer.backgroundColor === DEFAULT_FOOTER.backgroundColor) {
      this.backgroundColorBtn.setValue(ECMSFontSize.Standard);
    } else {
      this.backgroundColorBtn.setValue(ECMSFontSize.Benutzerdefiniert);
    }
    this.onBackgroundColorChange(this.backgroundColorBtn.value);
    this.footerForm.markAsDirty();
  }

  save(): boolean {
    if (!this.footerForm.valid) {
      this.footerForm.markAllAsTouched();
      return false;
    }
    this.footerStore.save(this.footerFormsFactory.buildEntity(this.footerForm));
    return true;
  }

  resetFormChanges(): void {
    this.footerStore.reload();
  }

  public handleAbortChangesDialog(): Observable<boolean> {
    const dialogRef = this.dialogService.openDialog<IDialogData>(
      ResetChangesDialogComponent,
      {
        headline: this.cmsTextProvider.shared.revertChanges,
        width: '600px',
        icon: 'warning',
        iconBackgroundColor: '#eed202',
      }
    );
    dialogRef.closed.subscribe(result => {
      if (result != undefined && result.submit) {
        if (result.acceptChanges) {
          this.save();
        } else {
          this.resetFormChanges();
        }
      }
    });
    return dialogRef.closed.pipe(map(result => result?.submit ?? false));
  }

  public moveItemAtIndex(
    formArray: FormArray,
    index: number,
    newIndex: number
  ): void {
    const control = formArray.at(index);

    formArray.removeAt(index, { emitEvent: false });
    formArray.insert(newIndex, control, { emitEvent: false });

    formArray.markAsDirty();
    formArray.updateValueAndValidity();
    this.footerForm.markAsDirty();
  }
}
